import { DELIVERY_CALENDAR_THEME, deliveryTargetKey, deliveryTargetLabel } from "./deliveryCalendar";
import { fullDeliveryTarget } from "./deliveryPlanningForm";
import {
    deliveryCheckboxStyle,
    deliveryCountBadgeStyle,
    deliveryEhpadButtonStyle,
    deliveryEhpadListStyle,
    deliveryEhpadPanelStyle,
    deliveryInputStyle,
    deliveryLinkButtonStyle,
    deliverySectionTitleStyle,
    deliverySelectedTargetStyle,
    deliverySubgroupButtonStyle,
} from "../styles/adminDashboardFormStyles";

function matchesEhpadSearch(ehpad, search) {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [ehpad.nom, ehpad.code, ehpad.tournee].some(value => String(value || "").toLowerCase().includes(q));
}

function checkboxMark(checked, hasSelectedSubgroup) {
    if (checked) return "✓";
    return hasSelectedSubgroup ? "•" : "";
}

// Ligne d'un EHPAD : cocher l'EHPAD entier ; le survol affiche ses sous-groupes.
function DeliveryEhpadOption({ ehpad, targets, active, onHover, onToggle }) {
    const fullTarget = fullDeliveryTarget(ehpad);
    const checked = targets.some(target => deliveryTargetKey(target) === deliveryTargetKey(fullTarget));
    const hasSelectedSubgroup = targets.some(target => target.ehpadId === ehpad.id && target.ehpadSubcategory);
    return (
        <button
            type="button"
            onMouseEnter={() => onHover(ehpad.id)}
            onFocus={() => onHover(ehpad.id)}
            onClick={() => onToggle(fullTarget)}
            style={deliveryEhpadButtonStyle(active)}
        >
            <span style={deliveryCheckboxStyle(checked, hasSelectedSubgroup)}>{checkboxMark(checked, hasSelectedSubgroup)}</span>
            <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", color: DELIVERY_CALENDAR_THEME.ink, fontSize: "15px", fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ehpad.nom}</span>
                <span style={{ display: "block", color: DELIVERY_CALENDAR_THEME.gray, fontSize: "13px", marginTop: "2px" }}>{ehpad.code} · {ehpad.tournee || "Sans tournée"}</span>
            </span>
            {(ehpad.productionSubcategories ?? []).length > 0 && (
                <span style={{ color: DELIVERY_CALENDAR_THEME.gray, fontSize: "18px", fontWeight: 900 }}>›</span>
            )}
        </button>
    );
}

// Sous-groupes de production de l'EHPAD survolé, sélectionnables un à un.
function DeliverySubgroups({ ehpad, targets, onToggle }) {
    return (
        <div style={{ borderTop: `1px solid ${DELIVERY_CALENDAR_THEME.border}`, paddingTop: "14px" }}>
            <div style={{ color: DELIVERY_CALENDAR_THEME.gray, fontSize: "13px", fontWeight: 800, marginBottom: "10px" }}>Sous-groupes de {ehpad.code}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {ehpad.productionSubcategories.map(subcategory => {
                    const target = fullDeliveryTarget(ehpad, subcategory);
                    const subChecked = targets.some(item => deliveryTargetKey(item) === deliveryTargetKey(target));
                    return (
                        <button key={subcategory} type="button" onClick={() => onToggle(target)} style={deliverySubgroupButtonStyle(subChecked)}>
                            {subcategory}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function DeliverySelectedTargets({ targets, onToggle }) {
    return (
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", minHeight: "34px" }}>
            {targets.length === 0 ? (
                <span style={{ color: DELIVERY_CALENDAR_THEME.gray, fontSize: "13px", fontWeight: 700 }}>Aucun EHPAD ajouté</span>
            ) : targets.map(target => (
                <button key={deliveryTargetKey(target)} type="button" onClick={() => onToggle(target)} title={`Retirer ${target.nom}`} style={deliverySelectedTargetStyle}>
                    {deliveryTargetLabel(target)}
                    <span style={{ marginLeft: "8px", color: DELIVERY_CALENDAR_THEME.validation }}>×</span>
                </button>
            ))}
        </div>
    );
}

// EHPAD inclus dans la livraison : recherche, sélection d'EHPAD entiers ou de sous-groupes, cibles retenues.
export function DeliveryEhpadPicker({ delivery, ehpads, loading }) {
    const { form, ehpadSearch, setEhpadSearch, hoveredEhpadId, setHoveredEhpadId, toggleDeliveryTarget } = delivery;
    const visibleEhpads = ehpads.filter(ehpad => matchesEhpadSearch(ehpad, ehpadSearch));
    const hoveredEhpad = visibleEhpads.find(ehpad => ehpad.id === hoveredEhpadId) ?? visibleEhpads[0] ?? null;
    return (
        <section style={{ display: "grid", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <h3 style={deliverySectionTitleStyle}>EHPADs inclus</h3>
                    <span style={deliveryCountBadgeStyle}>{form.targets.length}</span>
                </div>
                <div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
                    <button type="button" onClick={delivery.selectAllDeliveryTargets} style={deliveryLinkButtonStyle}>Tout sélectionner</button>
                    <button type="button" onClick={delivery.clearDeliveryTargets} style={deliveryLinkButtonStyle}>Tout désélectionner</button>
                </div>
            </div>

            <div style={deliveryEhpadPanelStyle}>
                <input
                    aria-label="Rechercher un EHPAD à ajouter"
                    style={deliveryInputStyle}
                    placeholder="Rechercher un EHPAD ou un sous-groupe..."
                    value={ehpadSearch}
                    onChange={e => setEhpadSearch(e.target.value)}
                />
                <div style={deliveryEhpadListStyle}>
                    {visibleEhpads.map(ehpad => (
                        <DeliveryEhpadOption
                            key={ehpad.id}
                            ehpad={ehpad}
                            targets={form.targets}
                            active={hoveredEhpad?.id === ehpad.id}
                            onHover={setHoveredEhpadId}
                            onToggle={toggleDeliveryTarget}
                        />
                    ))}
                    {!loading && visibleEhpads.length === 0 && (
                        <p style={{ margin: "10px 0 0", color: DELIVERY_CALENDAR_THEME.gray, fontSize: "14px", fontWeight: 700 }}>Aucun EHPAD trouvé.</p>
                    )}
                </div>

                {hoveredEhpad && (hoveredEhpad.productionSubcategories ?? []).length > 0 && (
                    <DeliverySubgroups ehpad={hoveredEhpad} targets={form.targets} onToggle={toggleDeliveryTarget} />
                )}
            </div>

            <DeliverySelectedTargets targets={form.targets} onToggle={toggleDeliveryTarget} />
        </section>
    );
}
